/**
 * "Ask about this report" (Rusty, 16 Oct): a side panel on the interactive
 * Leadership report where a leader asks questions of the report's data in
 * plain language.
 *
 * The answer comes only from the report's stored data (the newest refresh)
 * for the range and filters on screen: the server computes every number in
 * code through read-only tools and the model only explains them
 * (`POST /admin/reports/{name}/ask`, same viewers as the report). Each answer
 * says what it is for, names the views and figures it used, and links to the
 * section or opens the tickets behind a figure in the usual drill-in drawer.
 * A number the server could not match to the data is called out.
 *
 * The conversation is kept for the session (`sessionStorage`). Under each
 * answer, "Add a note" opens the note dialog with that question and answer
 * (a note or suggestion for the IT HelpDesk, in the Feedback inbox); when the
 * data can't answer, the panel says so and offers it first.
 */

import { Icon, NotebookPen, Sparkles } from "@norican/icons";
import { Alert, Badge, Button, Drawer, Field, Textarea, Typography } from "@norican/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { Markdown, useApiErrorMessage } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import {
  askReport as sdkAskReport,
  type InsightsMeasure,
  type LeadershipAskRequest,
  type LeadershipAskResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import {
  ASK_MAX,
  type AskCitationLike,
  type AskTurn,
  canAsk,
  canDrill,
  EXAMPLE_KEYS,
  historyForApi,
  parseHistory,
  sectionAnchor,
  serializeHistory,
  STORAGE_KEY,
  turnId,
} from "./ask-report-helpers";
import { apiQuery, type InsightsState } from "./insights-helpers";
import type { NoteContext } from "./note-dialog";
import { etTime, HELPDESK_OVERVIEW } from "./report-helpers";

export interface AskReportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: InsightsState;
  /** Open the drill-in drawer for a cited figure. */
  onOpenTickets: (measure: InsightsMeasure, value: string | null, title: string) => void;
  /** "Add a note" under an answer, with its question and answer. */
  onAddNote: (context: NoteContext) => void;
}

function loadHistory(): AskTurn[] {
  try {
    return parseHistory(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

function saveHistory(turns: AskTurn[]): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, serializeHistory(turns));
  } catch (error) {
    logger.debug("[leadership-ask] session storage unavailable", error);
  }
}

export function AskReportPanel({
  open,
  onOpenChange,
  state,
  onOpenTickets,
  onAddNote,
}: AskReportPanelProps) {
  const { t } = useT("common");
  const { language } = useLanguage();
  const errorMessage = useApiErrorMessage();
  const [turns, setTurns] = useState<AskTurn[]>(loadHistory);
  const [question, setQuestion] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);
  const A = (key: string, options?: Record<string, unknown>) =>
    t(`leadershipReport.askReport.${key}` as "leadershipReport.askReport.title", options);

  useEffect(() => {
    saveHistory(turns);
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [turns]);

  const ask = useMutation({
    mutationFn: async (body: LeadershipAskRequest): Promise<LeadershipAskResponse> => {
      const { data } = await sdkAskReport({
        path: { name: HELPDESK_OVERVIEW },
        body,
        throwOnError: true,
      });
      return data;
    },
    retry: false,
  });

  function send(text: string) {
    const trimmed = text.trim();
    if (!canAsk(trimmed) || ask.isPending) {
      return;
    }
    const history = historyForApi(turns);
    const mine: AskTurn = { id: turnId(), role: "user", text: trimmed };
    setTurns((prev) => [...prev, mine]);
    setQuestion("");
    ask.mutate(
      { ...apiQuery(state), question: trimmed, history, language },
      {
        onSuccess: (res) => {
          logger.debug("[leadership-ask] answered", res.feedback_id, res.tokens);
          setTurns((prev) => [
            ...prev,
            {
              id: turnId(),
              role: "assistant",
              text: res.answer,
              answerable: res.answerable,
              citations: res.citations ?? [],
              unchecked: res.unchecked ?? [],
              scope: res.scope,
              pulledAt: res.pulled_at,
            },
          ]);
        },
        onError: (error) => {
          logger.warn("[leadership-ask] question failed", error);
          // Keep the question so it can be sent again.
          setTurns((prev) => prev.filter((turn) => turn.id !== mine.id));
          setQuestion(trimmed);
        },
      },
    );
  }

  const failure = ask.isError ? errorMessage(ask.error) : null;

  return (
    <Drawer.Root side="right" modal="trap-focus" open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Viewport>
          <Drawer.Popup size="md" className="w-full" data-print-hide>
            <Drawer.Header>
              <Drawer.Title>{A("title")}</Drawer.Title>
              <Drawer.CloseButton label={t("button.close")} />
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
              <Typography variant="body-sm" tone="muted">
                {A("intro")}
              </Typography>
              {turns.length === 0 ? (
                <div className="flex flex-col gap-2">
                  <Typography variant="caption" tone="muted">
                    {A("examplesTitle")}
                  </Typography>
                  {EXAMPLE_KEYS.map((key) => (
                    <Button
                      key={key}
                      size="sm"
                      variant="outline"
                      tone="neutral"
                      className="h-auto justify-start py-1.5 text-left whitespace-normal"
                      startIcon={<Icon icon={Sparkles} size="sm" />}
                      disabled={ask.isPending}
                      onClick={() => send(A(key))}
                    >
                      {A(key)}
                    </Button>
                  ))}
                </div>
              ) : (
                <ul className="flex flex-col gap-4" aria-live="polite">
                  {turns.map((turn, index) => (
                    <TurnItem
                      key={turn.id}
                      turn={turn}
                      question={questionBefore(turns, index)}
                      onAddNote={(context) => {
                        onOpenChange(false);
                        onAddNote(context);
                      }}
                      pageRange={state.range}
                      onOpenTickets={(citation) => {
                        onOpenChange(false);
                        onOpenTickets(
                          citation.measure as InsightsMeasure,
                          citation.value ?? null,
                          citation.label,
                        );
                      }}
                      onGoTo={(anchor) => {
                        document
                          .getElementById(anchor)
                          ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
                      }}
                    />
                  ))}
                </ul>
              )}
              {ask.isPending ? (
                <Typography variant="body-sm" tone="muted" role="status">
                  {A("thinking")}
                </Typography>
              ) : null}
              {failure ? (
                <Alert tone="danger" title={failure.title}>
                  {failure.description}
                  {failure.traceLabel ? ` ${failure.traceLabel}` : ""}
                </Alert>
              ) : null}
              <div ref={endRef} />
            </Drawer.Body>
            <Drawer.Footer className="flex flex-col items-stretch gap-2">
              <Field name="leadership-report-ask" label={A("question")}>
                <Textarea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      send(question);
                    }
                  }}
                  rows={2}
                  autoResize
                  maxLength={ASK_MAX}
                />
              </Field>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={!canAsk(question) || ask.isPending}
                  onClick={() => send(question)}
                >
                  {ask.isPending ? A("sending") : A("send")}
                </Button>
                {turns.length > 0 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    disabled={ask.isPending}
                    onClick={() => setTurns([])}
                  >
                    {A("clear")}
                  </Button>
                ) : null}
              </div>
              <Typography variant="caption" tone="muted">
                {A("disclaimer")}
              </Typography>
            </Drawer.Footer>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** The question an answer at `index` replies to (the nearest user turn before it). */
function questionBefore(turns: AskTurn[], index: number): string {
  for (let i = index - 1; i >= 0; i -= 1) {
    const turn = turns[i];
    if (turn?.role === "user") {
      return turn.text;
    }
  }
  return "";
}

function TurnItem({
  turn,
  question,
  pageRange,
  onOpenTickets,
  onGoTo,
  onAddNote,
}: {
  turn: AskTurn;
  question: string;
  onAddNote: (context: NoteContext) => void;
  pageRange: string;
  onOpenTickets: (citation: AskCitationLike) => void;
  onGoTo: (anchor: string) => void;
}) {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const A = (key: string, options?: Record<string, unknown>) =>
    t(`leadershipReport.askReport.${key}` as "leadershipReport.askReport.title", options);

  if (turn.role === "user") {
    return (
      <li className="bg-bg-subtle self-end rounded-lg px-3 py-2">
        <Typography variant="caption" tone="muted">
          {A("you")}
        </Typography>
        <Typography variant="body-sm" className="break-words whitespace-pre-wrap">
          {turn.text}
        </Typography>
      </li>
    );
  }

  const rangeName = turn.scope
    ? t(`leadershipReport.live.range.${turn.scope.range}` as "leadershipReport.live.range.7d")
    : "";
  return (
    <li className="border-accent flex flex-col gap-2 border-l-4 pl-3">
      <Typography variant="caption" tone="muted">
        {A("assistant")}
      </Typography>
      {turn.answerable === false ? (
        <Badge tone="warning" variant="subtle" size="sm" className="self-start">
          {A("cannotAnswer")}
        </Badge>
      ) : null}
      <Markdown>{turn.text}</Markdown>
      {turn.answerable === false ? (
        <Typography variant="body-sm" tone="muted">
          {A("noteHint")}
        </Typography>
      ) : null}
      {turn.unchecked && turn.unchecked.length > 0 ? (
        <Alert tone="warning" title={A("unchecked", { numbers: turn.unchecked.join(", ") })} />
      ) : null}
      {turn.scope ? (
        <Typography variant="caption" tone="muted">
          {A("forScope", {
            range: rangeName,
            first: turn.scope.first_day,
            last: turn.scope.last_day,
            filters: turn.scope.filters === "none" ? A("noFilters") : turn.scope.filters,
            time: etTime(turn.pulledAt, locale) ?? "",
          })}
        </Typography>
      ) : null}
      {turn.citations && turn.citations.length > 0 ? (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="muted">
            {A("basedOn")}
          </Typography>
          <ul className="flex flex-col gap-1">
            {turn.citations.map((citation, index) => {
              const anchor = sectionAnchor(citation.section);
              return (
                <li
                  key={`${citation.section}-${index}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <Badge tone="neutral" variant="outline" size="sm">
                    {t(
                      `leadershipReport.askReport.section.${citation.section}` as "leadershipReport.askReport.section.tiles",
                    )}
                  </Badge>
                  <Typography variant="body-sm">{citation.label}</Typography>
                  {anchor ? (
                    <Button size="sm" variant="ghost" onClick={() => onGoTo(anchor)}>
                      {A("goTo")}
                    </Button>
                  ) : null}
                  {canDrill(citation, pageRange) && citation.count != null ? (
                    <Button size="sm" variant="ghost" onClick={() => onOpenTickets(citation)}>
                      {A("openTickets", { count: citation.count })}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <div>
        <Button
          size="sm"
          variant={turn.answerable === false ? "outline" : "ghost"}
          tone="neutral"
          startIcon={<Icon icon={NotebookPen} size="sm" />}
          onClick={() => onAddNote({ question, answer: turn.text })}
        >
          {A("addNote")}
        </Button>
      </div>
    </li>
  );
}
